<template>
    <Head title="Ticket Statistics & Analytics Report" />

    <div class="max-w-7xl mx-auto space-y-6 py-6 px-4 sm:px-6 lg:px-8">
        <!-- Page Header -->
        <div class="flex items-center justify-between">
            <div>
                <Link :href="route('reports.tickets.index')" class="text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 mb-2 inline-flex items-center">
                    <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                    </svg>
                    Back to Reports
                </Link>
                <h1 class="text-3xl font-bold text-gray-900 dark:text-gray-100">Statistics & Analytics</h1>
                <p class="mt-1 text-sm text-gray-600 dark:text-gray-400">
                    View comprehensive statistics and trends for tickets across different time periods
                </p>
            </div>
            <div v-if="canExport && statisticsData">
                <Button variant="secondary" @click="exportPdf">
                    <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Export PDF
                </Button>
            </div>
        </div>

        <!-- Loading State -->
        <Card v-if="loading">
            <div class="p-12 text-center">
                <svg class="animate-spin h-12 w-12 mx-auto text-indigo-600 mb-4" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <p class="text-sm text-gray-600 dark:text-gray-400">Loading statistics...</p>
            </div>
        </Card>

        <!-- Statistics Content -->
        <div v-else-if="statisticsData">
            <!-- Time Period Cards -->
            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <Card v-for="(data, period) in statisticsData.statistics" :key="period" class="bg-gradient-to-br from-indigo-50 to-indigo-100 dark:from-indigo-900/20 dark:to-indigo-800/20 border-indigo-200 dark:border-indigo-800">
                    <div class="p-6">
                        <h3 class="text-sm font-medium text-indigo-900 dark:text-indigo-300 mb-4">{{ formatPeriod(period) }}</h3>
                        <div class="space-y-3">
                            <div class="flex justify-between items-center">
                                <span class="text-xs text-indigo-700 dark:text-indigo-400">Total Tickets</span>
                                <span class="text-sm font-semibold text-indigo-900 dark:text-indigo-200">{{ data.total_tickets }}</span>
                            </div>
                            <div class="flex justify-between items-center">
                                <span class="text-xs text-green-700 dark:text-green-400">Resolved</span>
                                <span class="text-sm font-semibold text-green-900 dark:text-green-200">{{ data.resolved }}</span>
                            </div>
                            <div class="flex justify-between items-center">
                                <span class="text-xs text-blue-700 dark:text-blue-400">In Progress</span>
                                <span class="text-sm font-semibold text-blue-900 dark:text-blue-200">{{ data.in_progress }}</span>
                            </div>
                            <div class="flex justify-between items-center">
                                <span class="text-xs text-yellow-700 dark:text-yellow-400">Open</span>
                                <span class="text-sm font-semibold text-yellow-900 dark:text-yellow-200">{{ data.open }}</span>
                            </div>
                            <div class="flex justify-between items-center pt-2 border-t border-indigo-200 dark:border-indigo-700">
                                <span class="text-xs text-indigo-700 dark:text-indigo-400">Total Cost</span>
                                <span class="text-sm font-semibold text-indigo-900 dark:text-indigo-200">{{ formatMoney(data.total_cost) }}</span>
                            </div>
                            <div class="flex justify-between items-center">
                                <span class="text-xs text-indigo-700 dark:text-indigo-400">Avg Resolution</span>
                                <span class="text-sm font-semibold text-indigo-900 dark:text-indigo-200">{{ data.avg_resolution_time }} days</span>
                            </div>
                        </div>
                    </div>
                </Card>
            </div>

            <!-- Charts -->
            <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <!-- Status Distribution Chart -->
                <Card>
                    <div class="p-6">
                        <h3 class="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Status Distribution (All Time)</h3>
                        <BarChart
                            v-if="statisticsData.charts.status_distribution"
                            :data="prepareStatusChartData()"
                            :height="300"
                        />
                    </div>
                </Card>

                <!-- Priority Distribution Chart -->
                <Card>
                    <div class="p-6">
                        <h3 class="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Priority Distribution (All Time)</h3>
                        <BarChart
                            v-if="statisticsData.charts.priority_distribution"
                            :data="preparePriorityChartData()"
                            :height="300"
                        />
                    </div>
                </Card>
            </div>

            <!-- Monthly Trend Chart -->
            <Card>
                <div class="p-6">
                    <h3 class="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Monthly Trend (This Year)</h3>
                    <BarChart
                        v-if="statisticsData.charts.monthly_trend"
                        :data="prepareMonthlyChartData()"
                        :height="300"
                    />
                </div>
            </Card>

            <!-- Key Insights -->
            <Card v-if="statisticsData.statistics.this_year" class="bg-gradient-to-r from-amber-50 to-amber-100 dark:from-amber-900/20 dark:to-amber-800/20 border-amber-200 dark:border-amber-800">
                <div class="p-6">
                    <h3 class="text-lg font-semibold text-amber-900 dark:text-amber-300 mb-2">Key Insights</h3>
                    <p class="text-sm text-amber-800 dark:text-amber-400">
                        {{ statisticsData.statistics.this_year.resolved }} tickets resolved this year with an average resolution time of {{ statisticsData.statistics.this_year.avg_resolution_time }} days.
                        Total ticket cost for the year: {{ formatMoney(statisticsData.statistics.this_year.total_cost) }}
                    </p>
                </div>
            </Card>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { Head, Link } from '@inertiajs/vue3';
import Card from '@/Components/Common/Card.vue';
import Button from '@/Components/Common/Button.vue';
import BarChart from '@/Components/Charts/BarChart.vue';
import route from '@/Utils/route';
import { useToast } from '@/Composables/useToast';
import { useSecureAxios } from '@/Composables/useSecureAxios';
import { useCurrency } from '@/Composables/useCurrency';

const toast = useToast();
const axios = useSecureAxios();
const { formatMoney } = useCurrency();

const props = defineProps({
    canExport: Boolean,
});

// State
const loading = ref(true);
const statisticsData = ref(null);

// Methods
const loadStatistics = async () => {
    loading.value = true;

    try {
        const response = await axios.get(route('reports.tickets.statistics.load'));

        if (response.data.success) {
            statisticsData.value = response.data;
        }
    } catch (error) {
        toast.error('Failed to load statistics');
        console.error(error);
    } finally {
        loading.value = false;
    }
};

const exportPdf = async () => {
    try {
        const response = await axios.post(route('reports.tickets.export-pdf'), {
            report_type: 'statistics',
            data: statisticsData.value,
        }, {
            responseType: 'blob'
        });

        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `ticket-statistics-report-${new Date().toISOString().split('T')[0]}.pdf`);
        document.body.appendChild(link);
        link.click();
        link.remove();

        toast.success('PDF exported successfully');
    } catch (error) {
        toast.error('Failed to export PDF');
        console.error(error);
    }
};

const formatPeriod = (period) => {
    const periods = {
        '7_days': 'Last 7 Days',
        '30_days': 'Last 30 Days',
        '90_days': 'Last 90 Days',
        'this_year': 'This Year',
    };
    return periods[period] || period;
};

const prepareStatusChartData = () => {
    if (!statisticsData.value?.charts?.status_distribution) return null;

    const data = statisticsData.value.charts.status_distribution;

    return {
        labels: Object.keys(data).map(k => k.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())),
        datasets: [{
            label: 'Tickets',
            data: Object.values(data),
            backgroundColor: [
                'rgba(34, 197, 94, 0.8)',   // green - resolved
                'rgba(59, 130, 246, 0.8)',  // blue - in_progress
                'rgba(234, 179, 8, 0.8)',   // yellow - open/pending
                'rgba(249, 115, 22, 0.8)',  // orange - on_hold
                'rgba(239, 68, 68, 0.8)',   // red - cancelled
                'rgba(156, 163, 175, 0.8)', // gray - closed
                'rgba(99, 102, 241, 0.8)',  // indigo - assigned
                'rgba(236, 72, 153, 0.8)',  // pink - scheduled
            ],
        }],
    };
};

const preparePriorityChartData = () => {
    if (!statisticsData.value?.charts?.priority_distribution) return null;

    const data = statisticsData.value.charts.priority_distribution;

    return {
        labels: Object.keys(data).map(k => k.charAt(0).toUpperCase() + k.slice(1)),
        datasets: [{
            label: 'Tickets',
            data: Object.values(data),
            backgroundColor: [
                'rgba(34, 197, 94, 0.8)',   // green - low
                'rgba(59, 130, 246, 0.8)',  // blue - normal
                'rgba(249, 115, 22, 0.8)',  // orange - high
                'rgba(239, 68, 68, 0.8)',   // red - urgent
            ],
        }],
    };
};

const prepareMonthlyChartData = () => {
    if (!statisticsData.value?.charts?.monthly_trend) return null;

    const data = statisticsData.value.charts.monthly_trend;

    return {
        labels: Object.keys(data),
        datasets: [{
            label: 'Tickets',
            data: Object.values(data),
            backgroundColor: 'rgba(99, 102, 241, 0.8)',
        }],
    };
};

// Load statistics on mount
onMounted(() => {
    loadStatistics();
});
</script>
